/**
 * The one copy of the profile-generation prompts.
 *
 * They live in `shared/` because three packages generate profile prose from
 * them: the api resolver chain, the legacy client editor and the Town Square
 * inline editors. `api/src/ai/prompts/profile-prompts.ts` re-exports this
 * module, so every api importer keeps its path and there is still exactly one
 * definition of each prompt.
 *
 * The bio prompt reads `BIO_MAX_LENGTH` from `profile-fields` rather than
 * naming a number, so a generated bio can never exceed what a save accepts.
 */

import { LOCALE_DISPLAY_NAMES, type Locale } from './i18n';
import { BIO_MAX_LENGTH } from './profile-fields';

function localeInstruction(locale?: string | null): string {
  if (!locale || locale === 'en') {
    return '';
  }
  const name = LOCALE_DISPLAY_NAMES[locale as Locale];
  return name ? ` Generate the output in ${name}.` : '';
}

export const DISPLAY_NAME_PROMPT = (locale?: string | null) =>
  `Generate a display name for this artist. Use their first and last name if available, otherwise create one based on their profile, artwork, and artistic style. Return ONLY the display name, no explanation or additional text.${localeInstruction(locale)}`;

/** Bio text for profile/editing; keep under maxChars so downstream truncation rarely runs. */
export const bioPromptForMaxChars = (maxChars: number, locale?: string | null) => {
  const cap = Math.min(Math.max(Math.floor(maxChars), 50), 2000);
  const targetMin = Math.max(1, Math.floor(cap * 0.35));
  const targetMax = Math.max(targetMin, Math.floor(cap * 0.92));
  return `Generate a complete public artist bio based on their profile and artwork.

Requirements:
- Written in the first person, in the artist's own voice (use "I", "my", "me", never "they", "their", or "the artist")
- ${cap} characters or less (strict hard limit; stay safely under it so nothing is cut off)
- Aim for about ${targetMin}-${targetMax} characters
- End at a complete sentence; never trail off mid-sentence or mid-word
- If you are close to the limit, finish the current sentence and stop, do not start another sentence you cannot complete within the limit
- Keep it concise and memorable
- AVOID using the artist's name in the bio
- Do NOT use em dashes (—), en dashes (–), or semicolons. Use commas, periods, or rewrite the sentence instead

Return ONLY the bio text with no explanation or additional text.${localeInstruction(locale)}`;
};

// Generate to the same cap the write path enforces, so a generated bio is
// always savable.
export const BIO_PROMPT = (locale?: string | null) => bioPromptForMaxChars(BIO_MAX_LENGTH, locale);

export const STORY_PROMPT = (locale?: string | null) =>
  `Generate a narrative story in the artist's voice based on their profile, artwork, and creative journey.

The story must be between 1000 and 2000 characters, inclusive, and must end exactly at the end of a complete sentence. Do not cut off or trail mid-sentence or mid-word.

Write in clear, natural prose. Avoid flowery, poetic, or metaphor-heavy language. Keep the tone grounded and authentic. Do not discuss "perfection" or "perfectionism" as themes. Prefer concrete studio details over abstract philosophy.

Do not use markdown, lists, headings, or any other formatting.

Return only the story text. Do not include explanations, notes, or extra commentary.

If approaching the character limit, complete the current sentence and stop, even if that means ending slightly under 2000 characters. Never exceed 2000 characters. Target 1200-1800 characters so truncation is unnecessary.${localeInstruction(locale)}`;

export const CATEGORIES_PROMPT = `Analyze this artist's artwork and profile. Suggest 3-6 category names that best represent their work. Select category names from the available categories list provided. Return ONLY a JSON array of category names. Do not include any explanation, markdown, or additional text.`;

export const WELCOME_POST_PROMPT = (locale?: string | null) =>
  `Write a short, friendly introduction post from this artist for the ArtHelper community. They are joining for the first time and want to introduce themselves to other artists.

Requirements:
- Written in the first person, in the artist's own voice
- 280 characters or less (strict hard limit)
- Warm, casual, and authentic, not salesy or promotional
- Briefly mention what they make, why they make it, or what they're hoping to find here
- End at a complete sentence; never trail off mid-sentence or mid-word
- No hashtags, no markdown, no emojis, no links
- AVOID using the artist's name (the post will already be attributed to them)
- Do NOT use em dashes (—), en dashes (–), or semicolons. Use commas, periods, or rewrite the sentence instead

Return ONLY the post text with no explanation or additional text.${localeInstruction(locale)}`;

export const ARTIST_STATEMENT_PROMPT = (locale?: string | null) =>
  `Generate an artist statement for this artist based on their profile and overall body of work. Focus on their overall corpus of work, not individual pieces.

The statement should answer:
- what: the medium(s) they work in
- why: what they are excited about, their influences and inspirations
- how: their process or approach (if applicable)

Write in clear, professional prose suitable for curators and collectors. Avoid clichés, flowery language, and overly poetic metaphors. Never use the words "stroke", "strokes", "brushstroke" or "brushstrokes" (say brushwork, marks or gesture instead), or stock phrases like "the essence of", "a testament to", "a celebration of", "heart of", "embrace" and "timeless". Keep the tone grounded and authentic. Prefer short clear sentences. Do not use em dashes. Proofread for grammar before returning.

Do not use markdown, lists, headings, or any other formatting.

Return only the statement text. Do not include explanations, notes, or extra commentary.

The statement must be 5000 characters or less. If approaching the character limit, complete the current sentence and stop. Never exceed 5000 characters.${localeInstruction(locale)}`;

export const WELCOME_CRITIQUE_INTENT_PROMPT = (locale?: string | null) =>
  `Write a short critique request from this artist about the piece they just uploaded, to accompany it in a community critique post.

Requirements:
- Written in the first person, in the artist's own voice
- Between 150 and 400 characters (strict: never fewer than 150, never more than 400)
- Say what they were going for with the piece and what kind of feedback would help most (e.g. composition, technique)
- Warm and genuinely curious, not salesy
- End at a complete sentence; never trail off mid-sentence or mid-word
- No hashtags, no markdown, no emojis, no links
- AVOID using the artist's name
- Do NOT use em dashes (—), en dashes (–), or semicolons. Use commas, periods, or rewrite the sentence instead

Return ONLY the request text with no explanation or additional text.${localeInstruction(locale)}`;
